import React, { useState, useEffect } from 'react';
import { Bell, Home, RefreshCw, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { getNotifications } from '../lib/notification';
import NotificationItem from '../components/NotificationItem';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'text' | 'image' | 'video';
  media_url?: string;
  link_url?: string;
  created_at: string;
  is_read: boolean;
  user_id: string;
}

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, [retryCount]);

  async function fetchNotifications() {
    try {
      setLoading(true);
      setError(null);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('Usuário não autenticado');
      }

      // Use the getNotifications function from notification.ts
      const data = await getNotifications();
      setNotifications(data || []);
    } catch (err: any) {
      console.error('Erro ao buscar notificações:', err);
      setError(err.message || 'Erro ao buscar notificações. Tente novamente mais tarde.');
      
      // Implement exponential backoff for retries (max 3 retries)
      if (retryCount < 3) {
        const timeout = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
        }, timeout);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleMarkAsRead = () => {
    // Refresh notifications after marking as read
    fetchNotifications();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Bell className="h-8 w-8 text-brand-teal" />
          <h1 className="text-3xl font-bold text-brand-teal">Notificações</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchNotifications()}
            className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            title="Atualizar"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
          >
            <Home className="h-4 w-4" />
            <span>Voltar para Home</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Erro ao carregar notificações</p>
            <p className="mt-1">{error}</p>
            <button 
              onClick={() => setRetryCount(prev => prev + 1)}
              className="mt-2 text-red-700 underline"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
        </div>
      ) : notifications.length > 0 ? (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <NotificationItem 
              key={notification.id} 
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <Bell className="h-16 w-16 text-brand-teal/20 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brand-teal mb-2">
            Nenhuma notificação
          </h2>
          <p className="text-gray-600 max-w-md mx-auto">
            Você não tem notificações no momento. Fique atento para receber atualizações importantes da igreja.
          </p>
        </div>
      )}
    </div>
  );
}